/**
 * Editor de AUTORÍA de un bloque (los 6 tipos). Componente controlado: recibe
 * la `def` y emite cambios. Valida con el plugin del motor antes de que la UI
 * marque el bloque como válido (misma corrección que el player).
 */
import { type Component, createSignal, For, Match, Show, Switch } from "solid-js";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { blockRegistry } from "../../lib/registry";
// MEDIA-F3F: editor del bloque `video`
import { Film, Video as VideoIcon } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { VideoPickerDialog } from "@/features/media/components/video-picker-dialog";
import { useVideoAsset } from "@/features/media/hooks/use-media";

export interface BlockEditorProps {
  kind: string;
  def: Record<string, unknown>;
  onChange: (def: Record<string, unknown>) => void;
  /** MEDIA-F3F: curso para acotar la biblioteca de vídeos del bloque `video`. */
  courseId?: string;
}

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

/**
 * Each `<Match>` has narrowed `kind`, so it dispatches the shared `def` record to
 * the matching typed sub-editor. `def` is opaque authoring JSON, so this is a
 * validated trust-boundary cast.
 */
function dispatchDef(def: Record<string, unknown>): never {
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- validated def dispatched to its matching typed sub-editor
  return def as never;
}

/** Reads a string field off the opaque authoring `def`, defaulting to "". */
function strField(def: Record<string, unknown>, key: string): string {
  const v = def[key];
  return typeof v === "string" ? v : "";
}

export const BlockEditor: Component<BlockEditorProps> = (props) => {
  const { t } = useI18n();
  const patch = (p: Record<string, unknown>) => props.onChange({ ...props.def, ...p });
  const validation = () => {
    const plugin = blockRegistry.get(props.kind);
    return plugin
      ? plugin.validate(props.def)
      : { ok: false as const, errors: [t(msg`Unknown block type`)] };
  };
  const errors = (): string[] => {
    const v = validation();
    return v.ok ? [] : v.errors;
  };

  return (
    <div class="space-y-3">
      <Switch>
        <Match when={props.kind === "quiz"}>
          <QuizEditor def={dispatchDef(props.def)} patch={patch} />
        </Match>
        <Match when={props.kind === "flashcard"}>
          <label class="block text-xs font-medium text-muted-foreground">
            {t(msg`Front side`)}
          </label>
          <input
            class={field}
            data-testid="edit-front"
            value={strField(props.def, "front")}
            onInput={(e) => patch({ front: e.currentTarget.value })}
          />
          <label class="block text-xs font-medium text-muted-foreground">{t(msg`Back side`)}</label>
          <input
            class={field}
            data-testid="edit-back"
            value={strField(props.def, "back")}
            onInput={(e) => patch({ back: e.currentTarget.value })}
          />
        </Match>
        <Match when={props.kind === "fill-blank"}>
          <FillBlankEditor def={dispatchDef(props.def)} patch={patch} />
        </Match>
        <Match when={props.kind === "match"}>
          <MatchEditor def={dispatchDef(props.def)} patch={patch} />
        </Match>
        <Match when={props.kind === "code"}>
          <CodeEditor def={dispatchDef(props.def)} patch={patch} />
        </Match>
        <Match when={props.kind === "listen-type"}>
          <label class="block text-xs font-medium text-muted-foreground">
            {t(msg`Audio (URL or reference)`)}
          </label>
          <input
            class={field}
            data-testid="edit-audio"
            value={strField(props.def, "audioRef")}
            onInput={(e) => patch({ audioRef: e.currentTarget.value })}
          />
          <label class="block text-xs font-medium text-muted-foreground">
            {t(msg`Correct transcription`)}
          </label>
          <input
            class={field}
            data-testid="edit-transcript"
            value={strField(props.def, "transcript")}
            onInput={(e) => patch({ transcript: e.currentTarget.value })}
          />
        </Match>
        {/* ── MEDIA-F3F: bloque `video` ─────────────────────────────────── */}
        <Match when={props.kind === "video"}>
          <VideoBlockEditor def={dispatchDef(props.def)} patch={patch} courseId={props.courseId} />
        </Match>
        {/* ── end MEDIA-F3F ─────────────────────────────────────────────── */}
      </Switch>

      <Show when={errors().length > 0}>
        <ul
          class="rounded-lg bg-danger/10 px-3 py-2 text-xs text-danger"
          data-testid="block-errors"
        >
          <For each={errors()}>{(e) => <li>• {e}</li>}</For>
        </ul>
      </Show>
    </div>
  );
};

// ─── MEDIA-F3F: Video block editor ──────────────────────────────────────────

interface VideoBlockDef {
  videoAssetId?: string;
  minWatchPct?: number;
}

const VideoBlockEditor: Component<{
  def: VideoBlockDef;
  patch: (p: Record<string, unknown>) => void;
  courseId?: string;
}> = (props) => {
  const { t } = useI18n();
  const [pickerOpen, setPickerOpen] = createSignal(false);
  const asset = useVideoAsset(() => props.def.videoAssetId || undefined);
  const minPct = () => Math.round((props.def.minWatchPct ?? 0.9) * 100);
  return (
    <div class="space-y-3">
      <Show
        when={props.def.videoAssetId}
        fallback={<p class="text-xs text-muted-foreground">{t(msg`No video selected yet.`)}</p>}
      >
        <div class="flex items-center gap-2 text-sm" data-testid="video-block-selected">
          <Film class="h-4 w-4 text-accent" />
          <span class="truncate">{asset.data?.title ?? t(msg`Selected video`)}</span>
        </div>
      </Show>
      <Button
        variant="outline"
        size="sm"
        class="gap-1.5"
        data-testid="video-block-pick"
        disabled={!props.courseId}
        onClick={() => setPickerOpen(true)}
      >
        <VideoIcon class="h-4 w-4" />
        {props.def.videoAssetId ? t(msg`Change video`) : t(msg`Choose video`)}
      </Button>

      <label class="block text-xs font-medium text-muted-foreground">
        {t(msg`Watch {pct}% to complete`, { pct: minPct() })}
      </label>
      <input
        type="range"
        min="10"
        max="100"
        step="5"
        value={minPct()}
        data-testid="video-block-minwatch"
        class="w-full accent-accent"
        onInput={(e) => props.patch({ minWatchPct: Number(e.currentTarget.value) / 100 })}
      />

      <Show when={props.courseId}>
        {(cid) => (
          <VideoPickerDialog
            open={pickerOpen()}
            onOpenChange={setPickerOpen}
            courseId={cid()}
            onPick={(id) => props.patch({ videoAssetId: id })}
          />
        )}
      </Show>
    </div>
  );
};

// ─── Quiz editor ────────────────────────────────────────────────────────────

interface QuizDef {
  prompt?: string;
  options?: { id: string; text: string }[];
  correct?: string[];
  multiple?: boolean;
}

const QuizEditor: Component<{ def: QuizDef; patch: (p: Record<string, unknown>) => void }> = (
  props,
) => {
  const { t } = useI18n();
  const options = () => props.def.options ?? [];
  const correct = () => props.def.correct ?? [];
  const setOption = (id: string, text: string) =>
    props.patch({ options: options().map((o) => (o.id === id ? { ...o, text } : o)) });
  const addOption = () => {
    const id = String.fromCodePoint(97 + options().length);
    props.patch({ options: [...options(), { id, text: "" }] });
  };
  const toggleCorrect = (id: string) => {
    if (props.def.multiple) {
      const set = new Set(correct());
      set.has(id) ? set.delete(id) : set.add(id);
      props.patch({ correct: [...set] });
    } else {
      props.patch({ correct: [id] });
    }
  };
  return (
    <div class="space-y-3">
      <label class="block text-xs font-medium text-muted-foreground">{t(msg`Prompt`)}</label>
      <input
        class={field}
        data-testid="edit-prompt"
        value={props.def.prompt ?? ""}
        onInput={(e) => props.patch({ prompt: e.currentTarget.value })}
      />
      <label class="flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={props.def.multiple ?? false}
          onChange={(e) => props.patch({ multiple: e.currentTarget.checked, correct: [] })}
        />
        {t(msg`Multiple choice`)}
      </label>
      <div class="space-y-2">
        <For each={options()}>
          {(opt) => (
            <div class="flex items-center gap-2">
              <button
                type="button"
                data-testid={`edit-correct-${opt.id}`}
                onClick={() => toggleCorrect(opt.id)}
                class={`h-6 w-6 shrink-0 rounded-full border-2 ${correct().includes(opt.id) ? "border-success bg-success/20" : "border-border"}`}
                aria-label={t(msg`Mark as correct`)}
              >
                {correct().includes(opt.id) ? "✓" : ""}
              </button>
              <input
                class={field}
                data-testid={`edit-option-${opt.id}`}
                value={opt.text}
                onInput={(e) => setOption(opt.id, e.currentTarget.value)}
                placeholder={t(msg`Option {id}`, { id: opt.id })}
              />
            </div>
          )}
        </For>
      </div>
      <button
        type="button"
        data-testid="edit-add-option"
        onClick={addOption}
        class="text-xs text-primary hover:underline"
      >
        + {t(msg`Add option`)}
      </button>
    </div>
  );
};

// ─── Fill-blank editor ──────────────────────────────────────────────────────

interface FillDef {
  text?: string;
  blanks?: { id: string; answers: string[] }[];
}

const FillBlankEditor: Component<{ def: FillDef; patch: (p: Record<string, unknown>) => void }> = (
  props,
) => {
  const { t } = useI18n();
  const blanks = () => props.def.blanks ?? [];
  const setAnswer = (id: string, answers: string) =>
    props.patch({
      blanks: blanks().map((b) =>
        b.id === id
          ? {
              ...b,
              answers: answers
                .split("|")
                .map((s) => s.trim())
                .filter(Boolean),
            }
          : b,
      ),
    });
  const addBlank = () =>
    props.patch({ blanks: [...blanks(), { id: String(blanks().length + 1), answers: [""] }] });
  return (
    <div class="space-y-3">
      <label class="block text-xs font-medium text-muted-foreground">
        {t(msg`Text (use ___ for the blank)`)}
      </label>
      <textarea
        class={field}
        data-testid="edit-text"
        rows={3}
        value={props.def.text ?? ""}
        onInput={(e) => props.patch({ text: e.currentTarget.value })}
      />
      <For each={blanks()}>
        {(b, i) => (
          <div>
            <label class="block text-xs text-muted-foreground">
              {t(msg`Blank #{n} — accepted answers (separated by |)`, { n: i() + 1 })}
            </label>
            <input
              class={field}
              data-testid={`edit-blank-${b.id}`}
              value={b.answers.join(" | ")}
              onInput={(e) => setAnswer(b.id, e.currentTarget.value)}
            />
          </div>
        )}
      </For>
      <button
        type="button"
        data-testid="edit-add-blank"
        onClick={addBlank}
        class="text-xs text-primary hover:underline"
      >
        + {t(msg`Add blank`)}
      </button>
    </div>
  );
};

// ─── Match editor ───────────────────────────────────────────────────────────

interface MatchDef {
  pairs?: { left: string; right: string }[];
}

const MatchEditor: Component<{ def: MatchDef; patch: (p: Record<string, unknown>) => void }> = (
  props,
) => {
  const { t } = useI18n();
  const pairs = () => props.def.pairs ?? [];
  const setPair = (i: number, key: "left" | "right", value: string) =>
    props.patch({ pairs: pairs().map((p, idx) => (idx === i ? { ...p, [key]: value } : p)) });
  const addPair = () => props.patch({ pairs: [...pairs(), { left: "", right: "" }] });
  return (
    <div class="space-y-2">
      <For each={pairs()}>
        {(p, i) => (
          <div class="flex items-center gap-2">
            <input
              class={field}
              data-testid={`edit-pair-left-${i()}`}
              value={p.left}
              onInput={(e) => setPair(i(), "left", e.currentTarget.value)}
              placeholder={t(msg`Left`)}
            />
            <span class="text-muted-foreground">↔</span>
            <input
              class={field}
              data-testid={`edit-pair-right-${i()}`}
              value={p.right}
              onInput={(e) => setPair(i(), "right", e.currentTarget.value)}
              placeholder={t(msg`Right`)}
            />
          </div>
        )}
      </For>
      <button
        type="button"
        data-testid="edit-add-pair"
        onClick={addPair}
        class="text-xs text-primary hover:underline"
      >
        + {t(msg`Add pair`)}
      </button>
    </div>
  );
};

// ─── Code editor ────────────────────────────────────────────────────────────

interface CodeDef {
  prompt?: string;
  language?: string;
  checks?: { kind: string; value: string }[];
}

const CodeEditor: Component<{ def: CodeDef; patch: (p: Record<string, unknown>) => void }> = (
  props,
) => {
  const { t } = useI18n();
  const checks = () => props.def.checks ?? [];
  const setCheck = (i: number, value: string) =>
    props.patch({ checks: checks().map((c, idx) => (idx === i ? { ...c, value } : c)) });
  return (
    <div class="space-y-3">
      <label class="block text-xs font-medium text-muted-foreground">{t(msg`Prompt`)}</label>
      <input
        class={field}
        data-testid="edit-prompt"
        value={props.def.prompt ?? ""}
        onInput={(e) => props.patch({ prompt: e.currentTarget.value })}
      />
      <label class="block text-xs font-medium text-muted-foreground">{t(msg`Code language`)}</label>
      <input
        class={field}
        data-testid="edit-language"
        value={props.def.language ?? "text"}
        onInput={(e) => props.patch({ language: e.currentTarget.value })}
      />
      <label class="block text-xs font-medium text-muted-foreground">{t(msg`Must contain`)}</label>
      <For each={checks()}>
        {(c, i) => (
          <input
            class={field}
            data-testid={`edit-check-${i()}`}
            value={c.value}
            onInput={(e) => setCheck(i(), e.currentTarget.value)}
            placeholder={t(msg`Required snippet`)}
          />
        )}
      </For>
    </div>
  );
};
